<template>
  <div>
    <div v-if="!hideTitle" class="d-flex align-items-center mb-2">
      <em class="fas fa-info-circle text-orange mr-2" />
      <span class="color-secondary-29 font-14" style="font-weight: 600">
        Seguimiento de Plan
      </span>
    </div>
    <validation-observer ref="formRef">
      <div v-for="item in visibleQuestions" :key="`question-${item.id}`">
        <validation-provider v-slot="{ errors }" rules="required" mode="eager">
          <div class="d-flex align-items-center border-bottom">
            <div class="flex-grow-1">
              <div class="color-secondary-29">{{ item.question }}</div>
              <custom-error
                v-if="Boolean(errors[0])"
                class="ml-n1"
                message="Favor de seleccionar una opción."
                variant="no-border"
              />
            </div>
            <custom-radio
              v-model="item.value"
              is-question-list
              class="ordered-header-card-custom-radio custom-radio__extended"
              :class="{ 'custom-radio__extended--disabled': item.disabled }"
              custom-error-msg=""
              :name="item.name"
              :options="item.options"
              :change-event="(event) => handleChangeQuestion(event, item)"
              :disabled="item.disabled"
            />
          </div>
        </validation-provider>
      </div>
      <div v-if="lastQuestionFalsyMessage">
        <custom-error
          :message="lastQuestionFalsyMessage"
          variant="warning"
          :is-alert-mode="true"
        />
      </div>
      <div v-if="hasQualifiedStaffSelected" class="row mt-3">
        <div class="col">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
            mode="eager"
          >
            <custom-input
              v-model="staffName"
              label="Nombre"
              custom-error-msg="El nombre es requerido."
              :error="errors[0]"
              label-class="font-14"
              :disabled="readonly"
            />
          </validation-provider>
        </div>
        <div class="col">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
            mode="eager"
          >
            <custom-input
              label="Número de Puesto"
              v-model="staffPositionNumber"
              custom-error-msg="El número de puesto es requerido."
              :error="errors[0]"
              label-class="font-14"
              :disabled="readonly"
            />
          </validation-provider>
        </div>
      </div>
      <div v-if="!readonly || comment" class="row mt-3">
        <div class="col">
          <custom-text-area
            v-model="comment"
            :clearable="false"
            :disabled="readonly"
            custom-error-msg="Favor escribir un comentario."
            label="Comentario:"
            :maxlength="3000"
            :show-char-count="!readonly"
            :count-classes="['d-block text-right']"
            :rows="4"
          />
        </div>
      </div>
    </validation-observer>
    <div v-if="!readonly">
      <div class="d-flex justify-content-end mt-3">
        <action-button
          i="fas fa-times-circle"
          text="Cancelar"
          variant="outline-danger px-4 mr-3"
          @click="handleCancel"
        />
        <action-button
          i="fa fa-save"
          text="Guardar"
          variant="success px-4"
          @click="handleSave"
        />
      </div>
    </div>
  </div>
</template>

<script>
// libs
import CustomRadio from 'custom-radio';
import ActionButton from 'ActionButton';
import CustomError from 'custom-error';
import CustomInput from 'CustomInput';
import CustomTextArea from 'CustomTextArea';

// mixins
import alertMixin from '@/mixin/alert.mixin';

export default {
  name: 'FollowUp',
  mixins: [alertMixin],
  components: {
    CustomRadio,
    ActionButton,
    CustomError,
    CustomInput,
    CustomTextArea,
  },
  props: {
    initialData: {
      type: Object,
      default: () => ({}),
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isFromPrevious: {
      type: Boolean,
      default: false,
    },
    academicYearId: {
      type: Number,
      default: () => 0,
    },
  },
  data() {
    return {
      questions: [
        {
          id: 1,
          name: 'oreSchoolInfrastructureVisitCompletedFlag',
          question:
            '¿La ORE visitó la escuela para validar la infraestructura (salón) y sus necesidades?',
          options: [
            { id: 1, label: 'Sí', value: true, check: false },
            { id: 2, label: 'No', value: false, check: false },
          ],
          falsyMessage:
            'Se debe validar la infraestructura (salón) y sus necesidades',
          disabled: false,
        },
        {
          id: 2,
          name: 'classroomHasRequiredEquipmentForOpeningFlag',
          question:
            '¿El salón cuenta con equipos necesarios para la apertura de la alternativa de ubicación?',
          options: [
            { id: 1, label: 'Sí', value: true, check: false },
            { id: 2, label: 'No', value: false, check: false },
          ],
          falsyMessage: 'Se debe realizar una orden de compra.',
          disabled: false,
        },
        {
          id: 3,
          name: 'qualifiedStaffAvailableForPositionFlag',
          question: '¿Existe un personal cualificado para ocupar este puesto?',
          options: [
            { id: 1, label: 'Sí', value: true, check: false },
            { id: 2, label: 'No', value: false, check: false },
          ],
          falsyMessage:
            'Se debe enviar el informe de  solicitud de puestos para notificar la necesidad a Recursos Humanos.',
          disabled: false,
        },
      ],
      comment: null,
      staffName: undefined,
      staffPositionNumber: null,
    };
  },
  computed: {
    visibleQuestions() {
      const visible = this.questions.filter((question) => {
        if (!question.dependsOn) {
          return true;
        }
        const dependentQuestion = this.questions.find(
          (q) => q.name === question.dependsOn.question
        );
        if (!dependentQuestion) {
          return false;
        }
        const selectedOption = dependentQuestion.options.find(
          (opt) => opt.check === true
        );
        const selectedValue = selectedOption?.value;
        const shouldShow = selectedValue === question.dependsOn.value;
        return shouldShow;
      });
      return visible;
    },
    lastQuestionFalsyMessage() {
      const answeredQuestions = this.visibleQuestions.filter((question) => {
        const selectedOption = question.options.find(
          (opt) => opt.check === true
        );
        return selectedOption !== undefined;
      });
      if (answeredQuestions.length === 0) return null;
      const lastQuestion = answeredQuestions[answeredQuestions.length - 1];
      const selectedOption = lastQuestion.options.find(
        (opt) => opt.check === true
      );
      if (
        selectedOption &&
        selectedOption.value === false &&
        lastQuestion.falsyMessage
      ) {
        return lastQuestion.falsyMessage;
      }
      return null;
    },
    allQuestionsAnswered() {
      const questionsData = this.questions.map((question) => {
        const selectedOption = question.options.find(
          (opt) => opt.check === true
        );
        return {
          name: question.name,
          value: selectedOption ? selectedOption.value : null,
        };
      });
      return questionsData.length > 0 ? questionsData : null;
    },
    hasQualifiedStaffSelected() {
      const qualifiedStaffQuestion = this.questions.find(
        (question) => question.name === 'qualifiedStaffAvailableForPositionFlag'
      );
      if (!qualifiedStaffQuestion) return null;
      const selectedOption = qualifiedStaffQuestion.options.find(
        (opt) => opt.check === true
      );
      return selectedOption ? selectedOption.value === true : null;
    },
  },
  watch: {
    initialData: {
      handler(newData) {
        if (Object.values(newData).length) {
          this.comment = this.readonly ? newData.comment : null;
          this.questions.forEach((question) => {
            const selectedOption = newData[question.name];
            let canChange = false;

            switch (question.name) {
              case 'oreSchoolInfrastructureVisitCompletedFlag':
                canChange =
                  newData['canChangeOreSchoolInfrastructureVisitCompletedFlag'];
                break;
              case 'classroomHasRequiredEquipmentForOpeningFlag':
                canChange =
                  newData[
                    'canChangeClassroomHasRequiredEquipmentForOpeningFlag'
                  ];
                break;
              case 'qualifiedStaffAvailableForPositionFlag':
                canChange =
                  newData['canChageQualifiedStaffAvailableForPositionFlag'];
                break;
            }

            if (selectedOption !== undefined) {
              question.value = selectedOption;
              question.options.forEach((opt) => {
                opt.check = opt.value === selectedOption;
              });
              question.disabled =
                this.readonly ||
                (question.options.some((x) => x.check && x.value) &&
                  !canChange &&
                  this.isFromPrevious);
            }
          });
          this.$nextTick(() => {
            this.staffName = newData.staffName || undefined;
            this.staffPositionNumber = newData.staffPositionNumber || undefined;
          });
        }
      },
      immediate: true,
    },
    questions: {
      handler(newQuestions) {
        newQuestions.forEach((question) => {
          if (question.dependsOn) {
            const dependentQuestion = newQuestions.find(
              (q) => q.name === question.dependsOn.question
            );
            const selectedOption = dependentQuestion?.options?.find(
              (opt) => opt.check === true
            );
            const selectedValue = selectedOption?.value;
            if (
              !dependentQuestion ||
              selectedValue !== question.dependsOn.value
            ) {
              question.value = undefined;
              question.options.forEach((opt) => {
                opt.check = false;
              });
            }
            this.staffName = undefined;
            this.staffPositionNumber = undefined;
          }
        });
      },
      deep: true,
    },
  },
  methods: {
    handleChangeQuestion(value, item) {
      const question = item;
      if (question) {
        question.value = value.value;
        this.$forceUpdate();
      }
    },
    handleCancel() {
      this.$emit('cancel');
    },
    async handleSave() {
      const isValid = await this.$refs.formRef.validate();
      if (!isValid) {
        this.ShowToast(
          'Error',
          'Favor de completar los campos requeridos.',
          'error'
        );
        return;
      }
      const payload = {
        questions: this.allQuestionsAnswered,
        staffName: this.staffName,
        staffPositionNumber: this.staffPositionNumber,
        comment: this.comment,
      };
      this.$emit('save', { ...payload });
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables/_colors.scss';
::v-deep .custom-radio__extended {
  .custom-radio-container {
    > div {
      background: #f1f6f4;
      &:has(.custom-radio-button.bg-green) {
        background: transparent;
      }
    }
    .custom-radio-button {
      width: 92px !important;
      height: 70px !important;
      flex-shrink: 0 !important;
      .rounded-circle {
        .cr-icon {
          top: 50%;
          left: 16%;
        }
      }
      .step-label {
        font-size: 12px !important;
      }
    }
    .custom-radio-button {
      &.bg-green {
        background-color: map-get($colors, 'primary-1') !important;
      }
    }
  }
  &--disabled {
    .custom-radio-container {
      .custom-radio-button {
        &.bg-green {
          background-color: map-get($colors, 'secondary-30') !important;
        }
      }
    }
  }
}
::v-deep(.input-display) {
  margin-left: 0 !important;
}
</style>
